/**
 * ドメインモデル型 (Phase 2 最小版、docs/04 参照)。
 * 完全なスキーマは Phase 3 で展開、本ファイルは現状実装に必要な型のみ。
 */
// Sprint BG-DataSettings: 9 型 FieldDef + DataSettings + getModeTemplate 完全踏襲。
// 旧 v1 `old/packages/shared-types/src/index.ts` (L19-141) / `modeTemplates.ts` (251 行) を踏襲。
export * from './dataSettings.js';
// 選択肢「その他」の自由記入欄 ({key}__other に保存、表示は「その他（記入）」)。
export * from './fieldOtherText.js';
export * from './modeTemplates.js';
// Sprint BG-DataSettings 30 — ModeProfile (firestore-driven 初期テンプレート、modeTemplates の動的化版)。
export * from './modeProfile.js';
// 地区スコープ MCP サーバーのアクセストークン I/O 契約 (発行 / 一覧 / 失効、docs/13 + MCP連携仕様)。
export * from './mcpToken.js';
// 短縮アドレス (イベント配布用の期間限定アドレス `{origin}/s/{code}`、docs/27)。
export * from './shortLink.js';
// R-70: 端末リンク (linkedFromUids) から同一人物を 1 人に畳む純粋ヘルパー。
// 配分の参加者カウント / 配分先の手動選択 / ユーザー管理の一覧が共有する単一の真実源。
export * from './linkedParticipants.js';
// docs/27 §15: 机上調査の解析パイプライン v2 (設定の解決・プロンプト・選抜・結果の組立、pure)。
export * from './streetSurveyPipeline.js';
export * from './analysisRuns.js';
export * from './streetSurveys.js';
export * from './postAnalyses.js';
// docs/28: 写真の顔・ナンバープレート自動ぼかし (地区設定・投稿の処理状態・公開判定)。
export * from './photoPrivacy.js';
// docs/13 §9-3: AI 解析の 1 日上限 (既定値・上限値・地区設定の解決、functions と admin が共有)。
export * from './aiDailyQuota.js';
// issue #24 — AI 出力フォーマット要素 (分類軸/評価ラベル/集約スコア/テーブル) の投稿詳細表示可否。
// テンプレ既定 (AiOutputFormatSchema.postDetailVisibility) + 地区上書き (District.aiOutputDisplayOverrides)。
export * from './postDetailVisibility.js';
// LLM 世代 (v2/v3) と、世代ごとに有効な generationConfig 項目の正本。
// functions (送信項目の絞り込み) と admin (統合エディタの項目切替) が同じ判定を使うためここに置く。
export * from './aiModelGeneration.js';
// Sprint BG-DataSettings 6+7 (GAP-A4 / U-H8 / U-H9 / L17): ColorRule (value / quadrant) を v2 移植。
// 旧 `old/packages/shared-types/src/colorRules.ts` を v2 風に踏襲、admin DistrictColorRulePanel +
// user MapView marker / MapLegend で消費される。
export * from './colorRule.js';
/** admin 既定スタイルにユーザー上書きを merge した実効スタイルを返す (pure)。 */
export function resolveGisLayerStyle(base, override) {
    return override ? { ...base, ...override } : base;
}
/**
 * GisLayer の geometry 種別 + 実効スタイルから MapLibre layer 定義群を生成する (pure、要件3/4)。
 * admin プレビュー (G2) と user overlay (G3) で共用。`sourceId` は geojson source の id、
 * `idPrefix` はレイヤー id の接頭辞 (例 `gis-{layerId}`)。fill/line/circle/(label) を必要分だけ返す。
 */
export function buildGisMapLayers(opts) {
    const { idPrefix, sourceId, geometryType, style } = opts;
    const mixed = geometryType === 'mixed';
    const wantPolygon = geometryType === 'polygon' || mixed;
    const wantLine = geometryType === 'line' || mixed || wantPolygon; // polygon は輪郭線も描く
    const wantPoint = geometryType === 'point' || mixed;
    const out = [];
    const withZoom = (layer) => {
        if (style.minzoom !== undefined)
            layer.minzoom = style.minzoom;
        if (style.maxzoom !== undefined)
            layer.maxzoom = style.maxzoom;
        return layer;
    };
    // 色分け式: 段彩 (graduated) → 属性値別 (categorical) → 単色 (flat) の優先で解決。
    //   graduated: ['step', ['to-number', field, -1e30], default, lower0,c0, lower1,c1, …]
    //   categorical: ['match', ['to-string', field], v1,c1, …, default]
    const colorExpr = (flat) => {
        const grad = style.graduated;
        if (grad && grad.ranges.length > 0) {
            const arms = [];
            for (const r of grad.ranges)
                arms.push(r.lower, r.color);
            return ['step', ['to-number', ['get', grad.field], -1e30], grad.defaultColor, ...arms];
        }
        const cat = style.categorical;
        if (cat && cat.categories.length > 0) {
            const arms = [];
            for (const c of cat.categories)
                arms.push(c.value, c.color);
            return ['match', ['to-string', ['get', cat.field]], ...arms, cat.defaultColor];
        }
        return flat;
    };
    if (wantPolygon) {
        out.push({
            id: `${idPrefix}-fill`,
            interactive: true,
            layer: withZoom({
                id: `${idPrefix}-fill`,
                type: 'fill',
                source: sourceId,
                ...(mixed ? { filter: ['==', '$type', 'Polygon'] } : {}),
                paint: { 'fill-color': colorExpr(style.fillColor), 'fill-opacity': style.fillOpacity },
            }),
        });
    }
    if (wantLine) {
        out.push({
            id: `${idPrefix}-line`,
            interactive: true,
            layer: withZoom({
                id: `${idPrefix}-line`,
                type: 'line',
                source: sourceId,
                ...(mixed ? { filter: ['in', '$type', 'LineString', 'Polygon'] } : {}),
                paint: {
                    'line-color': colorExpr(style.lineColor),
                    'line-width': style.lineWidth,
                    'line-opacity': style.lineOpacity,
                    ...(style.lineDash && style.lineDash.length > 0
                        ? { 'line-dasharray': style.lineDash }
                        : {}),
                },
            }),
        });
    }
    if (wantPoint) {
        out.push({
            id: `${idPrefix}-circle`,
            interactive: true,
            layer: withZoom({
                id: `${idPrefix}-circle`,
                type: 'circle',
                source: sourceId,
                ...(mixed ? { filter: ['==', '$type', 'Point'] } : {}),
                paint: {
                    'circle-color': colorExpr(style.circleColor),
                    'circle-radius': style.circleRadius,
                    'circle-opacity': style.circleOpacity,
                    'circle-stroke-color': style.circleStrokeColor,
                    'circle-stroke-width': style.circleStrokeWidth,
                },
            }),
        });
    }
    // 線の方向矢印 (QGIS ArrowLine 近似、Tier2)。線本体 (上の line layer) に加え、
    // 矢じりアイコンを symbol-placement:line で線に沿って配置し、線の向きに自動回転させる。
    // icon-image は consumer (MapView) が `${idPrefix}-arrowimg` で矢印色のアイコンを addImage する規約。
    if (style.arrowEnabled && wantLine) {
        const sizePx = style.arrowSize && style.arrowSize > 0 ? style.arrowSize : 6;
        const iconSize = Math.max(0.6, Math.min(1.8, sizePx / 10));
        out.push({
            id: `${idPrefix}-arrow`,
            interactive: false,
            layer: withZoom({
                id: `${idPrefix}-arrow`,
                type: 'symbol',
                source: sourceId,
                ...(mixed ? { filter: ['==', '$type', 'LineString'] } : {}),
                layout: {
                    'symbol-placement': 'line',
                    'symbol-spacing': 60,
                    'icon-image': `${idPrefix}-arrowimg`,
                    'icon-size': iconSize,
                    'icon-rotation-alignment': 'map',
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                },
            }),
        });
    }
    // ラベル (任意属性をテキスト表示)。labelEnabled かつ labelField があるときのみ (reviewer ⚠️-7)。
    if (style.labelEnabled && style.labelField) {
        out.push({
            id: `${idPrefix}-label`,
            interactive: false,
            layer: withZoom({
                id: `${idPrefix}-label`,
                type: 'symbol',
                source: sourceId,
                layout: {
                    'text-field': ['to-string', ['get', style.labelField]],
                    // glyphs (Protomaps CDN) が提供し日本語も描画できる font を明示。既定 (Open Sans) は
                    // glyphs に無く文字が出ないため (docs/22 G8)。太字は Noto Sans Bold → Regular の fontstack
                    // (Bold グリフ欠落時も per-glyph で Regular に fallback し文字が消えない)。
                    'text-font': style.labelBold
                        ? ['Noto Sans Bold', 'Noto Sans Regular']
                        : ['Noto Sans Regular'],
                    'text-size': style.labelFontSize,
                    'text-anchor': 'top',
                    'text-offset': [0, 0.6],
                    'symbol-placement': geometryType === 'line' ? 'line' : 'point',
                },
                paint: {
                    'text-color': style.labelColor,
                    'text-halo-color': style.labelHaloColor,
                    'text-halo-width': style.labelHaloWidth ?? 1.2,
                },
            }),
        });
    }
    return out;
}
/**
 * Sprint BG-DataSettings 25 — `AiCategoryMaster` が指定 mode に紐付くかを判定するヘルパー。
 *
 *   - `master.modes` が定義されていれば includes 判定 (新方式)
 *   - 未定義時は `master.mode === target` 判定 (旧 minimum 互換)
 *
 * admin の M1 編集 UI で「同じ mode の M2 マスターのみ表示」する filter や、
 * 将来的に user app が district.mode から候補マスターを resolve する際に使用する。
 */
export function categoryMasterMatchesMode(master, target) {
    if (master.modes && master.modes.length > 0) {
        return master.modes.includes(target);
    }
    return master.mode === target;
}
/**
 * Sprint BG-DataSettings 25 — `AiCategoryMaster` の利用 mode を全て返す helper。
 * UI 表示 (例: `mode: ecw, walker`) や rules 検証で使う。`modes` 未指定なら `[mode]`。
 */
export function categoryMasterAllModes(master) {
    if (master.modes && master.modes.length > 0) {
        // mode は modes[0] 兼用なので、modes が存在する場合はそちらを正とする (重複除外)。
        return Array.from(new Set(master.modes));
    }
    return [master.mode];
}
/**
 * `AiClassificationAxis.useAsFilter` の既定挙動 (old2 `resolveAxisUseAsFilter` 踏襲)。
 * 明示指定があればそれを、無ければ primary/secondary 軸を既定でフィルタ対象とする
 * (tertiary は既定 off)。分析ツールのフィルタ UI / 集計軸生成で参照する。
 */
export function resolveAxisUseAsFilter(ax) {
    if (typeof ax.useAsFilter === 'boolean')
        return ax.useAsFilter;
    return ax.role === 'primary' || ax.role === 'secondary' || ax.role === undefined;
}
/** `AiAssessmentDef.useAsFilter` の既定挙動 (old2 踏襲、評価軸は既定でフィルタ対象)。 */
export function resolveAssessmentUseAsFilter(asm) {
    if (typeof asm.useAsFilter === 'boolean')
        return asm.useAsFilter;
    return true;
}
/** `AiAggregateScoresDef.useAsFilter` の既定挙動 (old2 踏襲、集約スコアは既定でフィルタ非対象)。 */
export function resolveAggregateUseAsFilter(agg) {
    if (typeof agg.useAsFilter === 'boolean')
        return agg.useAsFilter;
    return false;
}
/** 机上調査テンプレートのコレクション (docs/27 §17)。 */
export const STREET_SURVEY_TEMPLATES_COLLECTION = 'streetSurveyTemplates';
// issue #33 モードドリブン化: 出力フォーマット (M3) 駆動の分類軸解決 (モード非依存)。
// classificationAxes が空でも fields[] から軸を導出し、フラット構造の解析結果
// (classifications[] を持たない ECW 系) を分類セットへ正規化する。
export * from './aiFormatAxes.js';
/**
 * classificationAxes / assessment / reasoningField から classifications object-array の
 * itemFields を決定論的に派生する (docs/105、old2 deriveClassificationItemFields 移植)。
 *
 * 現行 promptBuilder.formatOutputSchema の classifications 要素 (rank → 各軸 code/name/sub →
 * assessment → assessment_level → score → reasoningField) と **同一のキー集合・順序** を
 * 生成する。これにより fields[] 未設定の既存フォーマットでも出力が変わらない (後方互換)。
 *
 * v2 の AiClassificationAxis は masterId / subFieldLabel を持たないため、定義文は label 駆動。
 */
export function deriveClassificationItemFields(schema) {
    const itemFields = [];
    itemFields.push({
        kind: 'text',
        key: 'rank',
        label: 'rank',
        definition: '重要度順位（1 が最重要）',
    });
    for (const ax of schema.classificationAxes ?? []) {
        itemFields.push({
            kind: 'text',
            key: ax.codeField,
            label: `${ax.label} コード`,
            definition: `マスタコード（${ax.label}）`,
        });
        itemFields.push({
            kind: 'text',
            key: ax.nameField,
            label: `${ax.label} 名`,
            definition: `日本語名（${ax.label}）`,
        });
        if (ax.subField) {
            itemFields.push({
                kind: 'enum-array',
                key: ax.subField,
                label: `${ax.label} 連鎖`,
                definition: '連鎖関係',
                source: { type: 'inline', values: [] },
            });
        }
    }
    const a = schema.assessment;
    if (a && (a.positiveLabel || a.negativeLabel)) {
        itemFields.push({
            kind: 'text',
            key: 'assessment',
            label: 'assessment',
            definition: `${a.positiveLabel} または ${a.negativeLabel}`,
        });
        if (a.levels?.length) {
            itemFields.push({
                kind: 'text',
                key: 'assessment_level',
                label: 'assessment_level',
                definition: a.levels.join(' または '),
            });
            itemFields.push({
                kind: 'text',
                key: 'score',
                label: 'score',
                definition: `符号付き整数（正=${a.positiveLabel}, 負=${a.negativeLabel}, 絶対値: ${a.levels
                    .map((l, i) => `${l}=${a.levels.length - i}`)
                    .join(', ')}）`,
            });
        }
    }
    if (schema.reasoningField) {
        itemFields.push({
            kind: 'text',
            key: schema.reasoningField,
            label: '根拠',
            definition: 'この分類セットの根拠',
        });
    }
    return itemFields;
}
/**
 * AI 出力フォーマットを「列名動的化レベル 3」の単一の真実の源 (`fields[]`) へ正規化する
 * (docs/105、old2 normalizeAiOutputFormat 移植)。読み取り側 (promptBuilder / validator /
 * 管理 UI) は必ずこの関数を経由して fields[] を参照する。
 *
 * - `schema.fields` が設定済み → それを採用 (object-array の itemFields が空なら軸から補完)。
 * - 未設定 (既存フォーマット) → classifications 型スケルトン
 *   (description / professional_insight / reasoning + classifications) を派生し、
 *   現行 (レベル 2.5) の自動生成出力と同一の列構成を再現する。
 *
 * 注意: `customSchemaText` 指定フォーマット (ECW フラット等) は promptBuilder が
 * 自動生成を bypass するため、本関数の legacy 派生は適用されない (classifications 型のみ対象)。
 */
/**
 * 防御的補完: `enum-array` / `code-list-string` は許可値の出どころ (`source`) が必須だが、
 * 手編集 JSON 取込・部分的な地区上書き・旧データ形状などで欠落することがある。欠落したまま
 * プロンプト生成 (formatOutputSchema) / 出力検証 (genericValidator) / admin エディタ
 * (cloneField) が `source.type` を読むと `Cannot read properties of undefined (reading 'type')`
 * でクラッシュするため、欠けていれば「空のインライン許可値」を補い、壊れたデータでも落ちない
 * ようにする。object-array は itemFields を再帰的に補完する。
 */
function ensureFieldSource(field) {
    if (field.kind === 'enum-array' || field.kind === 'code-list-string') {
        const source = field.source;
        if (!source)
            return { ...field, source: { type: 'inline', values: [] } };
        return field;
    }
    if (field.kind === 'object-array' && field.itemFields && field.itemFields.length > 0) {
        return { ...field, itemFields: field.itemFields.map(ensureFieldSource) };
    }
    return field;
}
export function normalizeAiOutputFormat(schema) {
    if (Array.isArray(schema.fields) && schema.fields.length > 0) {
        const filled = schema.fields.map((f) => f.kind === 'object-array' && (!f.itemFields || f.itemFields.length === 0)
            ? { ...f, itemFields: deriveClassificationItemFields(schema) }
            : ensureFieldSource(f));
        return { ...schema, fields: filled };
    }
    const fields = [
        {
            kind: 'text',
            key: 'description',
            label: 'description',
            definition: '画像から読み取った専門的な状況説明（日本語270〜330文字程度・目安）',
            lengthMin: 270,
            lengthMax: 330,
        },
        {
            kind: 'text',
            key: 'professional_insight',
            label: 'professional_insight',
            definition: '専門家の視点による一言での気づき・懸念（日本語30文字程度・目安）',
        },
        {
            kind: 'text',
            key: 'reasoning',
            label: 'reasoning',
            definition: '画像内の視覚的証拠に基づく推論プロセス（200〜400文字程度・目安）',
            lengthMin: 200,
            lengthMax: 400,
        },
        {
            kind: 'object-array',
            key: 'classifications',
            minItems: 1,
            maxItems: 10,
            itemFields: deriveClassificationItemFields(schema),
        },
    ];
    return { ...schema, fields };
}
/** プロンプトセクションのキー順 (assembleSystemPromptFromSections の固定順に一致)。 */
export const AI_PROMPT_SECTION_KEYS = [
    'role',
    'task',
    'contentRules',
    'classificationRules',
    'cautions',
    'rules',
    'outputSchemaNotes',
    'extraNotes',
];
/**
 * テンプレ (M1) ∪ 地区 override を解決して実効 AI 解析設定を返す (統合エディタ再設計 Phase 2)。
 *
 * マージ規則 (要件2「全」、ユーザー決定 2026-05-31):
 *   - outputFormatId: `outputFormatIdOverride` (trim 非空) 優先 → settings.outputFormatId
 *     (resolveOutputFormatId と同 semantics)
 *   - generationConfig / categoryMasterIds / promptInputMapping / validators / titleGeneration /
 *     inputFieldKeys: 対応 `*Override` が undefined でなければ override (空配列 `[]` も「明示的に空」
 *     として override 扱い)、無ければテンプレ値
 *   - outputFieldsOverride: 非空配列のときのみ override
 *   - promptSections: セクション単位で `override?.X !== undefined ? override : base`。
 *     `rules` は override 不可 (マスタ管理範囲、出力安全性)
 *
 * 由来 (source) は P4 統合エディタ地区モードの「グレー=継承 / 通常色=上書き」表示に使う。
 */
export function resolveEffectiveAiAnalysis(settings, district) {
    const d = district ?? undefined;
    const pick = (present, overrideVal, baseVal) => present ? { value: overrideVal, source: 'override' } : { value: baseVal, source: 'template' };
    const ofOverride = typeof d?.outputFormatIdOverride === 'string' ? d.outputFormatIdOverride.trim() : '';
    const outputFormatId = ofOverride
        ? { value: ofOverride, source: 'override' }
        : { value: settings.outputFormatId ? settings.outputFormatId : null, source: 'template' };
    // categoryMasterIds: 複数優先 → 後方互換単数 → 空
    const baseMasterIds = settings.categoryMasterIds?.length
        ? settings.categoryMasterIds
        : settings.categoryMasterId
            ? [settings.categoryMasterId]
            : [];
    const fieldsOv = d?.outputFieldsOverride;
    const outputFieldsOverride = Array.isArray(fieldsOv) && fieldsOv.length > 0
        ? { value: fieldsOv, source: 'override' }
        : { value: undefined, source: 'template' };
    // promptSections: per-section マージ (rules は override 不可)
    const baseSec = settings.promptSections ?? {};
    const ovSec = d?.promptSectionOverrides ?? {};
    const sectionValue = {};
    const perSection = {};
    for (const k of AI_PROMPT_SECTION_KEYS) {
        if (k === 'rules') {
            if (baseSec.rules !== undefined)
                sectionValue.rules = baseSec.rules;
            perSection.rules = 'template';
            continue;
        }
        const ov = ovSec[k];
        if (ov !== undefined) {
            sectionValue[k] = ov;
            perSection[k] = 'override';
        }
        else {
            if (baseSec[k] !== undefined)
                sectionValue[k] = baseSec[k];
            perSection[k] = 'template';
        }
    }
    return {
        outputFormatId,
        generationConfig: pick(d?.generationConfigOverride !== undefined, d?.generationConfigOverride, settings.generationConfig),
        categoryMasterIds: pick(d?.categoryMasterIdsOverride !== undefined, d?.categoryMasterIdsOverride, baseMasterIds),
        promptInputMapping: pick(d?.promptInputMappingOverride !== undefined, d?.promptInputMappingOverride, settings.promptInputMapping ?? []),
        validators: pick(d?.validatorsOverride !== undefined, d?.validatorsOverride, settings.validators ?? {}),
        titleGeneration: pick(d?.titleGenerationOverride !== undefined, d?.titleGenerationOverride, settings.titleGeneration),
        inputFieldKeys: pick(d?.inputFieldKeys !== undefined, d?.inputFieldKeys, settings.inputFieldKeys),
        outputFieldsOverride,
        promptSections: { value: sectionValue, perSection },
        systemInstructions: { value: settings.systemInstructions ?? '', source: 'template' },
    };
}
// アンケート (Survey、地区実体) は別 file で定義 (admin 第 22 弾、Phase 5)。
export * from './survey.js';
// AI 検証 (Verification、地区実体) は別 file で定義 (admin 第 23 弾、Phase 5)。
export * from './verification.js';
// M5 analysisDisplayConfigs.displayRules schema (Sprint AU、Phase 5)。
export { parseAnalysisDisplayRules, DEFAULT_POST_DETAIL_FIELDS } from './analysisDisplayConfig.js';
/**
 * 有効メインセッションを解決する純関数 (案1 統合、2026-06-16)。
 *
 * - `joinedSessionIds` のうち、現在 active なセッション (`activeSessionIdsInOrder`、表示順) に
 *   含まれるものだけを候補とする。
 * - 候補 0 → null (投稿は sessionId=null、地区が許可していれば投稿可)。
 * - 候補 1 → それを自動採用 (`mainSessionId` 未設定でも)。
 * - 候補が複数で `mainSessionId` が候補に含まれる → それを採用。
 * - 候補が複数で `mainSessionId` 未設定 / 候補外 (未参加 / archive 済を指す) → 表示順の先頭へフォールバック。
 *
 * `activeSessionIdsInOrder` は表示順 (sortSessions 済) で渡すこと。フォールバック先が安定する。
 */
export function resolveMainSession(joinedSessionIds, mainSessionId, activeSessionIdsInOrder) {
    const joinedSet = new Set(joinedSessionIds);
    const joinedActive = activeSessionIdsInOrder.filter((id) => joinedSet.has(id));
    if (joinedActive.length === 0)
        return null;
    if (joinedActive.length === 1)
        return joinedActive[0];
    if (mainSessionId && joinedActive.includes(mainSessionId))
        return mainSessionId;
    return joinedActive[0];
}
// ストリートビュー机上調査の調査項目・評価の動的解決 (docs/27 §9、pure)。
export * from './streetSurveyDisplay.js';
export * from './streetSurveyProjection.js';
//# sourceMappingURL=index.js.map